/**
 * The 32 languages LangShazam detects and transcribes — the single source of
 * truth for the /languages hub, the per-language SEO pages
 * (/languages/<slug>), the prerender, and the sitemap.
 *
 * Names match the backend's canonical names (backend/utils/lang_codes.py),
 * which is what the detector emits. Keep this module plain data: Node imports
 * it during the build, so no React and no JSX.
 */

export const LANGUAGES = [
  {
    slug: 'afrikaans', name: 'Afrikaans', code: 'af', nativeName: 'Afrikaans',
    family: 'West Germanic (Indo-European)', script: 'Latin',
    region: 'Southern Africa', speakers: '7M',
    countries: ['South Africa', 'Namibia'],
    confusedWith: ['Dutch'],
    greeting: 'Goeie dag',
    blurb: 'Afrikaans grew out of 17th-century Dutch in the Cape and is now one of South Africa\'s official languages. It sounds close to Dutch, which is exactly what makes it a good test of a language detector.',
  },
  {
    slug: 'arabic', name: 'Arabic', code: 'ar', nativeName: 'العربية',
    family: 'Semitic (Afro-Asiatic)', script: 'Arabic',
    region: 'Middle East & North Africa', speakers: '310M',
    countries: ['Egypt', 'Saudi Arabia', 'Morocco', 'Iraq', 'Algeria'],
    confusedWith: ['Persian', 'Urdu'],
    greeting: 'مرحبا (Marhaban)',
    blurb: 'Arabic spans dozens of regional dialects from Morocco to Iraq on top of Modern Standard Arabic. The detector identifies spoken Arabic across those varieties and transcribes it in Arabic script.',
  },
  {
    slug: 'cantonese', name: 'Cantonese', code: 'yue', nativeName: '廣東話',
    family: 'Sinitic (Sino-Tibetan)', script: 'Traditional Chinese characters',
    region: 'Southern China & Hong Kong', speakers: '85M',
    countries: ['Hong Kong', 'Macau', 'Guangdong (China)'],
    confusedWith: ['Chinese'],
    greeting: '你好 (Néih hóu)',
    blurb: 'Cantonese is the language of Hong Kong, Macau and Guangdong, with six to nine tones and a vocabulary that differs sharply from Mandarin in speech. LangShazam tells Cantonese and Mandarin apart from the audio itself.',
  },
  {
    slug: 'catalan', name: 'Catalan', code: 'ca', nativeName: 'Català',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Western Europe', speakers: '10M',
    countries: ['Spain (Catalonia, Valencia, Balearic Islands)', 'Andorra', 'France (Roussillon)'],
    confusedWith: ['Spanish', 'Italian'],
    greeting: 'Bon dia',
    blurb: 'Catalan is the official language of Andorra and co-official in Catalonia, Valencia and the Balearic Islands. It is frequently misheard as Spanish by generic tools; LangShazam recognises it as its own language.',
  },
  {
    slug: 'chinese', name: 'Chinese', code: 'zh', nativeName: '普通话 / 國語',
    family: 'Sinitic (Sino-Tibetan)', script: 'Han characters (Simplified and Traditional)',
    region: 'East Asia', speakers: '920M',
    countries: ['China', 'Taiwan', 'Singapore', 'Malaysia'],
    confusedWith: ['Cantonese'],
    greeting: '你好 (Nǐ hǎo)',
    blurb: 'Mandarin Chinese is the most spoken first language on earth. LangShazam detects Mandarin speech in real time and transcribes it in Chinese characters, distinguishing it from Cantonese.',
  },
  {
    slug: 'danish', name: 'Danish', code: 'da', nativeName: 'Dansk',
    family: 'North Germanic (Indo-European)', script: 'Latin',
    region: 'Northern Europe', speakers: '5.5M',
    countries: ['Denmark', 'Greenland', 'Faroe Islands'],
    confusedWith: ['Norwegian', 'Swedish'],
    greeting: 'Hej',
    blurb: 'Danish, Norwegian and Swedish are close enough that speakers can often follow one another, which makes them a classic language-detection challenge. LangShazam separates Danish from its Scandinavian neighbours from the audio.',
  },
  {
    slug: 'dutch', name: 'Dutch', code: 'nl', nativeName: 'Nederlands',
    family: 'West Germanic (Indo-European)', script: 'Latin',
    region: 'Western Europe', speakers: '24M',
    countries: ['Netherlands', 'Belgium (Flanders)', 'Suriname'],
    confusedWith: ['Afrikaans', 'German'],
    greeting: 'Hallo',
    blurb: 'Dutch is spoken in the Netherlands, Flanders and Suriname. It sits between German and English and shares much of its vocabulary with Afrikaans, its daughter language in South Africa.',
  },
  {
    slug: 'english', name: 'English', code: 'en', nativeName: 'English',
    family: 'West Germanic (Indo-European)', script: 'Latin',
    region: 'Worldwide', speakers: '380M',
    countries: ['United States', 'United Kingdom', 'Canada', 'Australia', 'India', 'Nigeria'],
    confusedWith: [],
    greeting: 'Hello',
    blurb: 'English is the most widely used second language in the world and comes in hundreds of accents. LangShazam recognises English speech from any region and produces a punctuated transcript as you talk.',
  },
  {
    slug: 'estonian', name: 'Estonian', code: 'et', nativeName: 'Eesti keel',
    family: 'Finnic (Uralic)', script: 'Latin',
    region: 'Northern Europe', speakers: '1.1M',
    countries: ['Estonia'],
    confusedWith: ['Finnish'],
    greeting: 'Tere',
    blurb: 'Estonian is a Finnic language, unrelated to its Latvian and Russian neighbours but a close cousin of Finnish. With only around a million speakers it is rarely supported well by speech tools; LangShazam detects it natively.',
  },
  {
    slug: 'finnish', name: 'Finnish', code: 'fi', nativeName: 'Suomi',
    family: 'Finnic (Uralic)', script: 'Latin',
    region: 'Northern Europe', speakers: '5.4M',
    countries: ['Finland', 'Sweden'],
    confusedWith: ['Estonian'],
    greeting: 'Hei',
    blurb: 'Finnish is famous for its fifteen grammatical cases and long compound words. It is not Indo-European at all, and its rhythm is distinctive enough that the detector usually names it within a single phrase.',
  },
  {
    slug: 'french', name: 'French', code: 'fr', nativeName: 'Français',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Worldwide', speakers: '280M',
    countries: ['France', 'Canada (Québec)', 'Belgium', 'Switzerland', 'DR Congo', 'Senegal'],
    confusedWith: [],
    greeting: 'Bonjour',
    blurb: 'French is an official language on five continents, from Québec to Kinshasa. LangShazam identifies French across those accents and transcribes it with accents and punctuation intact.',
  },
  {
    slug: 'galician', name: 'Galician', code: 'gl', nativeName: 'Galego',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Western Europe', speakers: '2.4M',
    countries: ['Spain (Galicia)'],
    confusedWith: ['Portuguese', 'Spanish'],
    greeting: 'Ola',
    blurb: 'Galician is spoken in the north-west corner of Spain and is a sister language of Portuguese. It is one of the hardest languages in our set to separate from its neighbours, and the detector still identifies it from the audio.',
  },
  {
    slug: 'german', name: 'German', code: 'de', nativeName: 'Deutsch',
    family: 'West Germanic (Indo-European)', script: 'Latin',
    region: 'Central Europe', speakers: '100M',
    countries: ['Germany', 'Austria', 'Switzerland', 'Luxembourg', 'Belgium'],
    confusedWith: ['Dutch'],
    greeting: 'Hallo',
    blurb: 'German is the most spoken native language in the European Union. LangShazam recognises standard German as well as Austrian and Swiss speech and transcribes it live.',
  },
  {
    slug: 'hebrew', name: 'Hebrew', code: 'he', nativeName: 'עברית',
    family: 'Semitic (Afro-Asiatic)', script: 'Hebrew',
    region: 'Middle East', speakers: '9M',
    countries: ['Israel'],
    confusedWith: ['Arabic'],
    greeting: 'שלום (Shalom)',
    blurb: 'Modern Hebrew was revived as an everyday spoken language in the late 19th century. It is written right to left in the Hebrew alphabet, and LangShazam both detects it and transcribes it in that script.',
  },
  {
    slug: 'hindi', name: 'Hindi', code: 'hi', nativeName: 'हिन्दी',
    family: 'Indo-Aryan (Indo-European)', script: 'Devanagari',
    region: 'South Asia', speakers: '345M',
    countries: ['India', 'Fiji', 'Nepal'],
    confusedWith: ['Urdu', 'Marathi'],
    greeting: 'नमस्ते (Namaste)',
    blurb: 'Hindi and Urdu are nearly the same language in casual speech but use different scripts and formal vocabulary. LangShazam detects Hindi speech and transcribes it in Devanagari.',
  },
  {
    slug: 'italian', name: 'Italian', code: 'it', nativeName: 'Italiano',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Southern Europe', speakers: '67M',
    countries: ['Italy', 'Switzerland (Ticino)', 'San Marino', 'Vatican City'],
    confusedWith: ['Spanish'],
    greeting: 'Ciao',
    blurb: 'Italian is the closest living language to Latin in vocabulary. Its open vowels are easy to mistake for Spanish on a short clip, which is where audio-native detection earns its keep.',
  },
  {
    slug: 'japanese', name: 'Japanese', code: 'ja', nativeName: '日本語',
    family: 'Japonic', script: 'Kanji, hiragana and katakana',
    region: 'East Asia', speakers: '125M',
    countries: ['Japan'],
    confusedWith: [],
    greeting: 'こんにちは (Konnichiwa)',
    blurb: 'Japanese uses three writing systems at once. LangShazam identifies Japanese speech within a phrase and returns a transcript mixing kanji and kana the way a native speaker would write it.',
  },
  {
    slug: 'korean', name: 'Korean', code: 'ko', nativeName: '한국어',
    family: 'Koreanic', script: 'Hangul',
    region: 'East Asia', speakers: '77M',
    countries: ['South Korea', 'North Korea'],
    confusedWith: [],
    greeting: '안녕하세요 (Annyeonghaseyo)',
    blurb: 'Korean is a language isolate with its own alphabet, Hangul, designed in the 15th century. LangShazam detects Korean speech and transcribes it in Hangul in real time.',
  },
  {
    slug: 'marathi', name: 'Marathi', code: 'mr', nativeName: 'मराठी',
    family: 'Indo-Aryan (Indo-European)', script: 'Devanagari',
    region: 'South Asia', speakers: '83M',
    countries: ['India (Maharashtra, Goa)'],
    confusedWith: ['Hindi'],
    greeting: 'नमस्कार (Namaskār)',
    blurb: 'Marathi is the language of Mumbai and Maharashtra. It shares the Devanagari script with Hindi, so text-based tools often confuse the two; LangShazam listens to the audio and tells them apart.',
  },
  {
    slug: 'norwegian', name: 'Norwegian', code: 'no', nativeName: 'Norsk (Bokmål)',
    family: 'North Germanic (Indo-European)', script: 'Latin',
    region: 'Northern Europe', speakers: '4.5M',
    countries: ['Norway'],
    confusedWith: ['Danish', 'Swedish'],
    greeting: 'Hei',
    blurb: 'Norwegian Bokmål is the written standard used by the large majority of Norwegians. Spoken Norwegian sits between Danish and Swedish, and LangShazam separates the three from the audio.',
  },
  {
    slug: 'norwegian-nynorsk', name: 'Norwegian Nynorsk', code: 'nn', nativeName: 'Nynorsk',
    family: 'North Germanic (Indo-European)', script: 'Latin',
    region: 'Northern Europe', speakers: '600K',
    countries: ['Norway (western districts)'],
    confusedWith: ['Norwegian', 'Danish'],
    greeting: 'Hei',
    blurb: 'Nynorsk is Norway\'s second official written standard, built from rural dialects in the 1800s rather than from Danish. LangShazam recognises Nynorsk-leaning speech and transcribes it in the Nynorsk standard.',
  },
  {
    slug: 'persian', name: 'Persian', code: 'fa', nativeName: 'فارسی',
    family: 'Iranian (Indo-European)', script: 'Perso-Arabic',
    region: 'Western Asia', speakers: '70M',
    countries: ['Iran', 'Afghanistan (Dari)', 'Tajikistan (Tajik)'],
    confusedWith: ['Arabic', 'Urdu'],
    greeting: 'سلام (Salām)',
    blurb: 'Persian, also called Farsi, is an Indo-European language written in a modified Arabic script. Sharing an alphabet with Arabic makes it a frequent false positive for text-based tools; LangShazam detects it from the sound.',
  },
  {
    slug: 'portuguese', name: 'Portuguese', code: 'pt', nativeName: 'Português',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Worldwide', speakers: '250M',
    countries: ['Brazil', 'Portugal', 'Angola', 'Mozambique'],
    confusedWith: ['Spanish', 'Galician'],
    greeting: 'Olá',
    blurb: 'Brazilian and European Portuguese sound quite different from each other, and both are regularly mistaken for Spanish. LangShazam detects Portuguese from either side of the Atlantic.',
  },
  {
    slug: 'romanian', name: 'Romanian', code: 'ro', nativeName: 'Română',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Southeast Europe', speakers: '24M',
    countries: ['Romania', 'Moldova'],
    confusedWith: ['Italian'],
    greeting: 'Bună',
    blurb: 'Romanian is the only Romance language in Eastern Europe, with Slavic loanwords layered over a Latin core. LangShazam identifies Romanian speech and transcribes it with its diacritics.',
  },
  {
    slug: 'russian', name: 'Russian', code: 'ru', nativeName: 'Русский',
    family: 'East Slavic (Indo-European)', script: 'Cyrillic',
    region: 'Eastern Europe & Central Asia', speakers: '150M',
    countries: ['Russia', 'Belarus', 'Kazakhstan', 'Kyrgyzstan', 'Ukraine'],
    confusedWith: [],
    greeting: 'Здравствуйте (Zdravstvuyte)',
    blurb: 'Russian is the most spoken Slavic language and a lingua franca across the former Soviet Union. LangShazam detects Russian speech and returns a Cyrillic transcript in real time.',
  },
  {
    slug: 'spanish', name: 'Spanish', code: 'es', nativeName: 'Español',
    family: 'Romance (Indo-European)', script: 'Latin',
    region: 'Worldwide', speakers: '475M',
    countries: ['Mexico', 'Spain', 'Colombia', 'Argentina', 'United States'],
    confusedWith: ['Portuguese', 'Catalan', 'Italian'],
    greeting: 'Hola',
    blurb: 'Spanish is the second most spoken native language in the world. LangShazam recognises Castilian and Latin American Spanish alike and transcribes them as you speak.',
  },
  {
    slug: 'swedish', name: 'Swedish', code: 'sv', nativeName: 'Svenska',
    family: 'North Germanic (Indo-European)', script: 'Latin',
    region: 'Northern Europe', speakers: '10M',
    countries: ['Sweden', 'Finland'],
    confusedWith: ['Norwegian', 'Danish'],
    greeting: 'Hej',
    blurb: 'Swedish has a musical pitch accent that sets it apart from Danish and Norwegian even when the words look similar on paper. LangShazam detects Swedish from that sound and transcribes it live.',
  },
  {
    slug: 'turkish', name: 'Turkish', code: 'tr', nativeName: 'Türkçe',
    family: 'Turkic', script: 'Latin',
    region: 'Western Asia', speakers: '80M',
    countries: ['Türkiye', 'Cyprus', 'Germany (diaspora)'],
    confusedWith: [],
    greeting: 'Merhaba',
    blurb: 'Turkish builds long words by stacking suffixes and follows strict vowel harmony. LangShazam identifies Turkish speech in seconds and transcribes it with the ş, ğ, ı and ö characters in place.',
  },
  {
    slug: 'urdu', name: 'Urdu', code: 'ur', nativeName: 'اردو',
    family: 'Indo-Aryan (Indo-European)', script: 'Perso-Arabic (Nastaliq)',
    region: 'South Asia', speakers: '70M',
    countries: ['Pakistan', 'India'],
    confusedWith: ['Hindi', 'Persian'],
    greeting: 'السلام علیکم (Assalām-u-alaikum)',
    blurb: 'Urdu is Pakistan\'s national language and shares its everyday grammar with Hindi while using Perso-Arabic script and Persian vocabulary. LangShazam detects Urdu speech and transcribes it in Nastaliq-style script.',
  },
  {
    slug: 'vietnamese', name: 'Vietnamese', code: 'vi', nativeName: 'Tiếng Việt',
    family: 'Vietic (Austroasiatic)', script: 'Latin with tone marks',
    region: 'Southeast Asia', speakers: '85M',
    countries: ['Vietnam'],
    confusedWith: [],
    greeting: 'Xin chào',
    blurb: 'Vietnamese has six tones and is written in a Latin alphabet loaded with diacritics. LangShazam detects Vietnamese from its tonal sound and transcribes it with every tone mark.',
  },
  {
    slug: 'xhosa', name: 'Xhosa', code: 'xh', nativeName: 'isiXhosa',
    family: 'Nguni (Bantu, Niger-Congo)', script: 'Latin',
    region: 'Southern Africa', speakers: '8M',
    countries: ['South Africa (Eastern Cape, Western Cape)'],
    confusedWith: ['Zulu'],
    greeting: 'Molo',
    blurb: 'Xhosa is known for its click consonants, written with c, q and x. It is mutually intelligible with Zulu to a large degree, and LangShazam distinguishes the two from the audio.',
  },
  {
    slug: 'zulu', name: 'Zulu', code: 'zu', nativeName: 'isiZulu',
    family: 'Nguni (Bantu, Niger-Congo)', script: 'Latin',
    region: 'Southern Africa', speakers: '12M',
    countries: ['South Africa (KwaZulu-Natal, Gauteng)'],
    confusedWith: ['Xhosa'],
    greeting: 'Sawubona',
    blurb: 'Zulu is the most widely spoken home language in South Africa. Like Xhosa it uses click consonants, and separating the two is one of the harder cases the detector handles.',
  },
];

export const LANGUAGE_BY_SLUG = Object.fromEntries(LANGUAGES.map((l) => [l.slug, l]));
export const LANGUAGE_BY_NAME = Object.fromEntries(LANGUAGES.map((l) => [l.name, l]));

/** Sibling languages to link from a language page: same family first, then same region. */
export function relatedLanguages(lang, limit = 6) {
  const out = [];
  const push = (l) => {
    if (l.slug !== lang.slug && !out.includes(l)) out.push(l);
  };
  lang.confusedWith.forEach((name) => LANGUAGE_BY_NAME[name] && push(LANGUAGE_BY_NAME[name]));
  LANGUAGES.filter((l) => l.family === lang.family).forEach(push);
  LANGUAGES.filter((l) => l.region === lang.region).forEach(push);
  return out.slice(0, limit);
}
